import React, { useMemo } from "react";
import { useLanguage } from "../../contexts/LanguageContext";
import { AdminField } from "./AdminFormDialog";
import { Input, SelectNative } from "../ui/input";
import { DocumentUpload } from "../subcomponents/DocumentUpload";
import { ContentMediaPreview } from "../subcomponents/ContentMediaPreview";
import {
  inferMediaType,
  MediaAttachmentLayout,
  MediaAttachmentType,
} from "../../utils/contentMedia";
import type { CloudinaryFolder } from "../../utils/cloudinary";

export type MediaAttachmentForm = {
  attachmentType: string;
  attachmentUrl: string;
  attachmentLayout: MediaAttachmentLayout;
};

type Props = {
  attachmentType: string;
  attachmentUrl: string;
  attachmentLayout: MediaAttachmentLayout;
  folder?: CloudinaryFolder;
  setFieldValue: (field: string, value: unknown) => void;
};

export const AdminMediaAttachment: React.FC<Props> = ({
  attachmentType,
  attachmentUrl,
  attachmentLayout,
  folder = "ulpgl/articles",
  setFieldValue,
}) => {
  const { t } = useLanguage();

  const previewMedia = useMemo(() => {
    if (!attachmentType || !attachmentUrl.trim()) return null;
    if (!["youtube", "video", "pdf"].includes(attachmentType)) return null;
    return {
      attachmentType: attachmentType as MediaAttachmentType,
      attachmentUrl: attachmentUrl.trim(),
      attachmentLayout,
    };
  }, [attachmentType, attachmentUrl, attachmentLayout]);

  const onUrlChange = (url: string) => {
    setFieldValue("attachmentUrl", url);
    if (url.trim()) {
      const inferred = inferMediaType(url);
      if (inferred) setFieldValue("attachmentType", inferred);
    }
  };

  return (
    <div className="border rounded-lg p-4 bg-muted/30 grid gap-4" data-testid="media-attachment-block">
      <div>
        <p className="text-sm font-semibold m-0">{t.admin.mediaAttachment}</p>
        <p className="text-xs text-muted-foreground mt-1 mb-0">{t.admin.mediaAttachmentHelp}</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <AdminField label={t.admin.mediaType}>
          <SelectNative
            value={attachmentType}
            data-testid="attachment-type"
            onChange={(e) => {
              const next = e.target.value;
              setFieldValue("attachmentType", next);
              if (!next) setFieldValue("attachmentUrl", "");
            }}
          >
            <option value="">{t.admin.mediaNone}</option>
            <option value="youtube">YouTube</option>
            <option value="video">{t.admin.mediaVideoLink}</option>
            <option value="pdf">PDF</option>
          </SelectNative>
        </AdminField>
        <AdminField label={t.admin.mediaLayout}>
          <SelectNative
            value={attachmentLayout}
            data-testid="attachment-layout"
            onChange={(e) => setFieldValue("attachmentLayout", e.target.value)}
          >
            <option value="auto">{t.admin.mediaLayoutAuto}</option>
            <option value="portrait">{t.admin.mediaLayoutPortrait}</option>
            <option value="landscape">{t.admin.mediaLayoutLandscape}</option>
          </SelectNative>
        </AdminField>
      </div>

      {attachmentType === "pdf" ? (
        <DocumentUpload
          value={attachmentUrl}
          onChange={onUrlChange}
          folder={folder}
          label={t.admin.mediaPdfUpload}
          testId="attachment-pdf"
        />
      ) : attachmentType ? (
        <AdminField label={t.admin.mediaUrl}>
          <Input
            value={attachmentUrl}
            data-testid="attachment-url"
            placeholder={
              attachmentType === "youtube"
                ? "https://www.youtube.com/watch?v=…"
                : "https://… (MP4, Vimeo, etc.)"
            }
            onChange={(e) => onUrlChange(e.target.value)}
          />
        </AdminField>
      ) : null}

      {previewMedia && (
        <div>
          <p className="text-xs font-medium text-muted-foreground mb-2">{t.admin.mediaPreview}</p>
          <ContentMediaPreview media={previewMedia} compact />
        </div>
      )}
    </div>
  );
};
